
import { useState, useEffect } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/hooks/use-toast'
import type { CustomTask, CreateCustomTaskData } from '@/types/tasks'

export const useCustomTasks = () => {
  const [customTasks, setCustomTasks] = useState<CustomTask[]>([])
  const [loading, setLoading] = useState(true)
  const { toast } = useToast()

  const fetchCustomTasks = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      
      if (!session) {
        setLoading(false)
        return
      }

      const { data: customTasksData, error: customError } = await supabase
        .from('custom_tasks')
        .select('*')
        .eq('user_id', session.user.id)

      if (customError) throw customError
      setCustomTasks(customTasksData || [])
    } catch (error: any) {
      console.error('Error fetching custom tasks:', error)
      toast({
        title: "Error",
        description: "Failed to load custom tasks",
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  const createCustomTask = async (taskData: CreateCustomTaskData) => {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) return

      const { error } = await supabase
        .from('custom_tasks')
        .insert({
          user_id: session.user.id,
          ...taskData
        })

      if (error) throw error

      toast({ title: "Custom task created" })
      fetchCustomTasks()
    } catch (error: any) {
      console.error('Error creating task:', error)
      toast({
        title: "Error",
        description: "Failed to create task",
        variant: "destructive",
      })
    }
  }

  useEffect(() => {
    fetchCustomTasks()

    // Set up real-time subscription
    const channel = supabase
      .channel('custom-tasks-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'custom_tasks'
        },
        () => {
          fetchCustomTasks()
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [])

  return {
    customTasks,
    loading,
    createCustomTask,
    refreshCustomTasks: fetchCustomTasks
  }
}
